import React, { useState, useEffect, useRef } from 'react';
import { StatsPanel } from './components/StatsPanel';
import { MathProblem } from './components/MathProblem';
import { StickerCollection } from './components/StickerCollection';
import { GameSettings } from './components/GameSettings';
import { ExerciseHistory } from './components/ExerciseHistory';
import { RewardsPanel } from './components/RewardsPanel';
import { CookieConsent } from './components/CookieConsent';
import { Footer } from './components/Footer';
import { stickers } from './data/stickers';
import type { Operation, GameStats, GameSettings as GameSettingsType, Difficulty, Exercise, Reward } from './types';

function App() {
  const [stats, setStats] = useState<GameStats>(() => {
    try {
      const saved = localStorage.getItem('mathGameStats');
      const defaultStats: GameStats = {
        correct: 0,
        incorrect: 0,
        stickersCollected: [],
        history: [],
        points: 0,
        timeLimit: null,
        timeStart: null,
        rewards: []
      };
      
      if (!saved) return defaultStats;
      
      const parsed = JSON.parse(saved);
      return {
        ...defaultStats,
        ...parsed,
        history: Array.isArray(parsed.history) ? parsed.history : [],
        rewards: Array.isArray(parsed.rewards) ? parsed.rewards : []
      };
    } catch (error) {
      console.warn('Failed to parse saved stats:', error);
      return {
        correct: 0,
        incorrect: 0,
        stickersCollected: [],
        history: [],
        points: 0,
        timeLimit: null,
        timeStart: null,
        rewards: []
      };
    }
  });

  const [settings, setSettings] = useState<GameSettingsType>(() => {
    try {
      const saved = localStorage.getItem('mathGameSettings');
      const defaultSettings: GameSettingsType = {
        difficulty: 'easy',
        mixedOperations: false,
        selectedOperations: ['add']
      };
      
      if (!saved) return defaultSettings;
      
      const parsed = JSON.parse(saved);
      return {
        ...defaultSettings,
        ...parsed,
        selectedOperations: Array.isArray(parsed.selectedOperations) && parsed.selectedOperations.length > 0
          ? parsed.selectedOperations
          : ['add']
      };
    } catch (error) {
      console.warn('Failed to parse saved settings:', error);
      return {
        difficulty: 'easy',
        mixedOperations: false,
        selectedOperations: ['add']
      };
    }
  });

  const [showSticker, setShowSticker] = useState(false);
  const [lastSticker, setLastSticker] = useState<number | null>(null);
  const mathInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      try {
        localStorage.setItem('mathGameStats', JSON.stringify(stats));
      } catch (error) {
        console.warn('Failed to save stats:', error);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [stats]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      try {
        localStorage.setItem('mathGameSettings', JSON.stringify(settings));
      } catch (error) {
        console.warn('Failed to save settings:', error);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [settings]);

  useEffect(() => {
    if (stats.timeLimit && stats.timeStart) {
      const interval = setInterval(() => {
        const now = Date.now();
        const endTime = stats.timeStart! + stats.timeLimit! * 60 * 1000;
        
        if (now >= endTime) {
          setStats(prev => ({
            ...prev,
            timeLimit: null,
            timeStart: null
          }));
        }
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [stats.timeLimit, stats.timeStart]);

  const handleCookieAccept = (cookieSettings: { necessary: boolean; analytics: boolean; marketing: boolean }) => {
    if (cookieSettings.analytics) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        'gtm.start': new Date().getTime(),
        event: 'gtm.js'
      });
    }
  };

  const handleCookieReject = () => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'cookieConsent',
      cookieConsent: 'rejected'
    });
  };

  const getPointsForDifficulty = (difficulty: Difficulty): number => {
    switch (difficulty) {
      case 'easy': return 1;
      case 'medium': return 2;
      case 'hard': return 3;
    }
  };

  const handleCorrectAnswer = (exercise: Omit<Exercise, 'id' | 'timestamp' | 'isCorrect'>) => {
    const points = getPointsForDifficulty(settings.difficulty);
    const availableStickers = stickers
      .filter(s => !stats.stickersCollected.includes(s.id))
      .map(s => s.id);
    
    if (availableStickers.length > 0) {
      const randomSticker = availableStickers[Math.floor(Math.random() * availableStickers.length)];
      setLastSticker(randomSticker);
      setShowSticker(true);
      
      setTimeout(() => {
        setShowSticker(false);
        setStats(prev => ({
          ...prev,
          correct: prev.correct + 1,
          points: prev.points + points,
          stickersCollected: [...prev.stickersCollected, randomSticker],
          history: [
            ...prev.history,
            {
              ...exercise,
              id: crypto.randomUUID(),
              timestamp: Date.now(),
              isCorrect: true
            }
          ]
        }));

        // Sprawdź czy urządzenie nie jest mobilne przed ustawieniem fokusu
        const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        if (!isMobile && mathInputRef.current) {
          mathInputRef.current.focus();
        }
      }, 2000);
    }
  };

  const handleWrongAnswer = (exercise: Omit<Exercise, 'id' | 'timestamp' | 'isCorrect'>) => {
    setStats(prev => ({
      ...prev,
      incorrect: prev.incorrect + 1,
      history: [
        ...prev.history,
        {
          ...exercise,
          id: crypto.randomUUID(),
          timestamp: Date.now(),
          isCorrect: false
        }
      ]
    }));
  };

  const handleReset = () => {
    if (window.confirm('Czy na pewno chcesz zresetować cały postęp?')) {
      setStats({
        correct: 0,
        incorrect: 0,
        stickersCollected: [],
        history: [],
        points: 0,
        timeLimit: null,
        timeStart: null,
        rewards: []
      });
    }
  };

  const handleRewardAdd = (reward: Reward) => {
    setStats(prev => ({
      ...prev,
      rewards: [...prev.rewards, reward].sort((a, b) => a.points - b.points)
    }));
  };

  const handleRewardRemove = (points: number) => {
    setStats(prev => ({
      ...prev,
      rewards: prev.rewards.filter(r => r.points !== points)
    }));
  };

  const handleTimeSet = (minutes: number) => {
    setStats(prev => ({
      ...prev,
      timeLimit: minutes,
      timeStart: Date.now()
    }));
  };

  return (
    <>
      <div className="min-h-screen bg-gray-100 p-2 sm:p-4 flex flex-col">
        <div className="max-w-[1920px] mx-auto flex-grow">
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-center mb-4 sm:mb-6">
            Matematyczna przygoda
          </h1>
          
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-12 gap-3 sm:gap-4">
            <div className="md:order-1 xl:col-span-3 space-y-3 sm:space-y-4">
              <GameSettings
                settings={settings}
                onSettingsChange={setSettings}
              />
              
              <MathProblem
                settings={settings}
                onCorrectAnswer={handleCorrectAnswer}
                onWrongAnswer={handleWrongAnswer}
                inputRef={mathInputRef}
              />
              
              <RewardsPanel
                stats={stats}
                onRewardAdd={handleRewardAdd}
                onRewardRemove={handleRewardRemove}
                onTimeSet={handleTimeSet}
                onReset={handleReset}
              />
            </div>

            <div className="md:order-3 xl:order-2 xl:col-span-6 md:col-span-2">
              <StickerCollection collectedStickers={stats.stickersCollected} />
            </div>

            <div className="md:order-2 xl:order-3 xl:col-span-3 space-y-3 sm:space-y-4">
              <StatsPanel stats={stats} onReset={handleReset} />
              <ExerciseHistory exercises={stats.history} />
            </div>
          </div>
        </div>
        <Footer />
      </div>

      {showSticker && lastSticker && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white p-4 sm:p-6 rounded-lg shadow-xl transform animate-bounce max-w-[280px] sm:max-w-sm w-full">
            <h3 className="text-lg sm:text-xl font-bold text-center mb-3 sm:mb-4">Brawo! Nowa naklejka!</h3>
            <img
              src={stickers.find(s => s.id === lastSticker)?.url}
              alt="New sticker"
              className="w-24 h-24 sm:w-32 sm:h-32 object-contain rounded-lg mx-auto"
            />
          </div>
        </div>
      )}

      <CookieConsent
        onAccept={handleCookieAccept}
        onReject={handleCookieReject}
      />
    </>
  );
}

export default App;